import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import "../styles/ManageAdvertisingAgent.css";
import SideBar from "../components/SideBar";
import { useNavigate } from "react-router-dom";
import { businessAgentAPI } from "../services/businessAgentService";
import {
  formatToDDMMYYYY,
  convertHTMLDateToDDMMYYYY,
  convertDDMMYYYYToHTMLDate,
} from "../utils/dateUtils";

function BusinessAgentApprovedHistory() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [ApprovedDate, setApprovedDate] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalApprovedAgents, setTotalApprovedAgents] = useState(0);
  const [filteredAgentsCount, setFilteredAgentsCount] = useState(0);
  const [currentPageAgentsCount, setCurrentPageAgentsCount] = useState(0);
  const [activeAgentsCount, setActiveAgentsCount] = useState(0);
  const [inactiveAgentsCount, setInactiveAgentsCount] = useState(0);
  const itemsPerPage = 6;
  const navigate = useNavigate();

  // Fetch approved applications
  const fetchApprovedApplications = async () => {
    setLoading(true);
    setError("");
    try {
      const filters = {
        page: currentPage,
        per_page: itemsPerPage,
        status: "approved",
        agent_type: type !== "all" ? type : undefined,
        search: search || undefined,
      };

      const response = await businessAgentAPI.getAgentApplications(filters);
      setApplications(response.applications || []);
      
      // Use pagination total as fallback if getBusinessAgentCount fails
      if (response.pagination && response.pagination.total && totalApprovedAgents === 0) {
        setTotalApprovedAgents(response.pagination.total);
      }
      
      // Calculate active/inactive agents from the data
      if (response.applications && response.applications.length > 0) {
        let active = 0;
        let inactive = 0;
        
        response.applications.forEach(app => {
          if (app.agent_details && app.agent_details.is_active) {
            active++;
          } else {
            inactive++;
          }
        });
        
        setActiveAgentsCount(active);
        setInactiveAgentsCount(inactive);
      }
    } catch (err) {
      setError("Failed to fetch approved applications");
      console.error("Error fetching approved applications:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch total approved business agent count with filters
  const fetchTotalApprovedAgentCount = async () => {
    try {
      // Prepare filters for the count API
      const filters = {};
      
      if (search) {
        filters.search = search;
      }
      
      if (type !== 'all') {
        filters.agent_type = type;
      }
      
      // Count only approved applications for this page
      filters.status = "approved";
      
      const response = await businessAgentAPI.getBusinessAgentCount(filters);
      
      // Check different possible response structures
      if (response.count !== undefined) {
        setTotalApprovedAgents(response.count);
      } else if (response.total !== undefined) {
        setTotalApprovedAgents(response.total);
      } else if (response.business_count !== undefined) {
        setTotalApprovedAgents(response.business_count);
      } else if (response.approved_agents !== undefined) {
        setTotalApprovedAgents(response.approved_agents);
      } else {
        // If the response structure is different, try to extract count from the first property
        const count = Object.values(response)[0];
        if (typeof count === 'number') {
          setTotalApprovedAgents(count);
        } else {
          console.warn('Unexpected API response format:', response);
          // Fallback: fetch count from pagination in fetchApprovedApplications
        }
      }
    } catch (error) {
      console.error('Error fetching approved business agent count:', error);
      // Don't show error to user for count failure, use fallback
    }
  };

  // Fetch business agent statistics
  const fetchAgentStats = async () => {
    try {
      const stats = await businessAgentAPI.getAgentStats();
      if (stats && stats.statistics) {
        setActiveAgentsCount(stats.statistics.active_agents || 0);
        setInactiveAgentsCount(stats.statistics.inactive_agents || 0);
      }
    } catch (error) {
      console.error('Error fetching agent stats:', error);
    }
  };

  useEffect(() => {
    fetchTotalApprovedAgentCount();
    fetchAgentStats();
  }, [search, type]);

  useEffect(() => {
    fetchApprovedApplications();
  }, [currentPage, type, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, type, ApprovedDate]);

  // Calculate filtered agents count whenever applications change
  useEffect(() => {
    if (applications.length > 0) {
      // Filter applications based on frontend filters
      const filtered = applications.filter((application) => {
        // Extract name from customer_details
        const customerName = application.customer_details 
          ? `${application.customer_details.first_name || ''} ${application.customer_details.last_name || ''}`.trim()
          : application.customer_name || '';
        
        const searchMatch =
          customerName.toLowerCase().includes(search.toLowerCase()) ||
          (application.customer_details?.email || '')
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (application.email || '')
            .toLowerCase()
            .includes(search.toLowerCase());

        const typeMatch =
          type === "all" ||
          application.agent_type?.toLowerCase() === type.toLowerCase();

        // Date filtering in dd/mm/yyyy format
        const approvedDate = formatToDDMMYYYY(application.reviewed_at);
        const dateMatch = ApprovedDate === "all" || approvedDate === ApprovedDate;

        return searchMatch && dateMatch && typeMatch;
      });

      // Calculate paginated agents
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

      // Update statistics
      setFilteredAgentsCount(filtered.length);
      setCurrentPageAgentsCount(paginated.length);
    } else {
      setFilteredAgentsCount(0);
      setCurrentPageAgentsCount(0);
    }
  }, [applications, currentPage, search, type, ApprovedDate]);

  const goToRequestHistory = () => {
    navigate("/manage-business-agent");
  };

  const goToApprovedHistory = () => {
    navigate("/business-agent-approved-history");
  };

  const goToRejectedHistory = () => {
    navigate("/business-agent-rejected-history");
  };

  // Handle date filter change
  const handleDateChange = (e) => {
    const htmlDate = e.target.value;
    const ddmmyyyyDate = convertHTMLDateToDDMMYYYY(htmlDate);
    setApprovedDate(ddmmyyyyDate);
  };

  // Get current HTML date value for the input
  const getHTMLDateValue = () => {
    return ApprovedDate === "all"
      ? ""
      : convertDDMMYYYYToHTMLDate(ApprovedDate);
  };

  // Filter applications by approved date
  const filteredApplications = applications.filter((application) => {
    // Extract name from customer_details
    const customerName = application.customer_details 
      ? `${application.customer_details.first_name || ''} ${application.customer_details.last_name || ''}`.trim()
      : application.customer_name || '';
    
    const searchMatch =
      customerName.toLowerCase().includes(search.toLowerCase()) ||
      (application.customer_details?.email || '')
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (application.email || '')
        .toLowerCase()
        .includes(search.toLowerCase());

    const typeMatch =
      type === "all" ||
      application.agent_type?.toLowerCase() === type.toLowerCase();

    // Date filtering in dd/mm/yyyy format
    const approvedDate = formatToDDMMYYYY(application.reviewed_at);
    const dateMatch = ApprovedDate === "all" || approvedDate === ApprovedDate;

    return searchMatch && dateMatch && typeMatch;
  });

  const totalPages = Math.ceil((totalApprovedAgents || 0) / itemsPerPage);
  const paginatedApplications = filteredApplications; // Already paginated from API

  const handleRowClick = (application) => {
    // Prepare agent data for details page
    const agentData = {
      ...application.customer_details, // Spread customer details
      application_id: application.application_id || application.id, // Add application_id to agent object
      agent_type: application.agent_type,
      status: application.status,
      applied_at: application.applied_at || application.created_at,
      reviewed_at: application.reviewed_at,
      rejection_reason: application.rejection_reason,
      photo_url: application.photo_url,
      is_already_agent: application.is_already_agent,
      // Include additional fields for business agent
      business_name: application.business_name || application.customer_details?.business_name,
      experience_years: application.experience_years || application.customer_details?.experience_years,
      first_name: application.first_name || application.customer_details?.first_name,
      last_name: application.last_name || application.customer_details?.last_name,
      email: application.email || application.customer_details?.email,
      phone_number: application.phone_number || application.customer_details?.phone_number,
      // Include agent details if available
      agent_code: application.agent_details?.agent_code,
      commission_rate: application.agent_details?.commission_rate,
      is_active: application.agent_details?.is_active,
    };

    navigate("/business-agent-details", {
      state: {
        agent: agentData,
        application: application, // Pass full application data
        isApprovedAgent: true, // Flag to indicate this is an approved agent
      },
    });
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Business Agent</h2>
          </div>

          {/* Approved History Statistics Section */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Approved Applications:</span>
              <span className="stat-value">{totalApprovedAgents}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Applications:</span>
              <span className="stat-value">{filteredAgentsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {paginatedApplications.length}
              </span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by Type:</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="mini">Mini Business Agent</option>
                <option value="senior">Senior Business Agent</option>
              </select>
            </div>

            <div className="input-group">
              <label>Filter by Approved Date:</label>
              <input
                type="date"
                value={getHTMLDateValue()}
                onChange={handleDateChange}
                className="date-filter-select"
               maxLength={255} />
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
               maxLength={20} />
            </div>
          </div>

          <div className="agent-top-bar">
            <div className="top-buttons">
              <button className="tab-btn" onClick={goToRequestHistory}>
                Request History
              </button>
              <button className="tab-btn active" onClick={goToApprovedHistory}>
                Approved History
              </button>
              <button className="tab-btn" onClick={goToRejectedHistory}>
                Rejected History
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Approved Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Approved Agents...</div>
                  </td>
                </tr>
              ) : paginatedApplications.length > 0 ? (
                paginatedApplications.map((application, i) => {
                  // Get the customer name from application data
                  const customerName = application.customer_details 
                    ? `${application.customer_details.first_name || ''} ${application.customer_details.last_name || ''}`.trim()
                    : application.first_name 
                      ? `${application.first_name} ${application.last_name || ''}`.trim()
                      : 'N/A';
                  
                  const customerEmail = application.customer_details?.email || application.email || 'N/A';
                  const agentType = application.agent_type === "senior"
                    ? "Senior Business Agent"
                    : "Mini Business Agent";
                  const approvedDate = formatToDDMMYYYY(application.reviewed_at);
                  const isActive = application.agent_details?.is_active;
                  
                  return (
                    <tr
                      key={application.application_id || application.id || i}
                      onClick={() => handleRowClick(application)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{customerName}</td>
                      <td>{customerEmail}</td>
                      <td>{agentType}</td>
                      <td>{approvedDate}</td>
                      <td>
                        <span className={`status ${isActive ? 'active' : 'inactive'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    {filteredAgentsCount === 0 && totalApprovedAgents > 0 
                      ? "No approved agents match your filters" 
                      : "No approved agents found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination - Only show when there are filtered applications */}
          {!loading && filteredAgentsCount > 0 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BusinessAgentApprovedHistory;